// =================================================================
// FilePreviewModal.js — Preview contenu fichier + lancement analyse
// =================================================================

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, FileText, Eye, Search, BarChart3, Clock, CheckCircle, AlertTriangle, XCircle, Download } from 'lucide-react';

const FilePreviewModal = ({ projectId, fileId, fileName, onClose, onAnalyze }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('text'); // text | markdown
  const [expandedAnalysis, setExpandedAnalysis] = useState(null);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const token = localStorage.getItem('oracle_token');
        const res = await axios.get(`/api/projects/${projectId}/files/${fileId}/content`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.error || 'Erreur chargement');
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, [projectId, fileId]);

  const getQualityBadge = (status, sourceType) => {
    if (sourceType === 'none' || status === 'failed') return { label: 'Non extrait', color: '#ef4444', icon: <XCircle size={12} /> };
    if (status === 'ready' && (sourceType === 'full_text' || sourceType === 'ocr_text')) return { label: 'Complet', color: '#16a34a', icon: <CheckCircle size={12} /> };
    if (status === 'ready' && sourceType === 'reconstructed_chunks') return { label: 'Reconstruit', color: '#f59e0b', icon: <AlertTriangle size={12} /> };
    if (status === 'partial') return { label: 'Partiel', color: '#f59e0b', icon: <AlertTriangle size={12} /> };
    if (sourceType === 'summary_only') return { label: 'Résumé seul', color: '#6b7280', icon: <Eye size={12} /> };
    return { label: 'Non extrait', color: '#ef4444', icon: <XCircle size={12} /> };
  };

  const badge = data ? getQualityBadge(data.extraction_status, data.source_type) : null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000
    }} onClick={onClose}>
      <div style={{
        background: 'var(--modal-bg, #1a1a2e)', border: '1px solid var(--card-border, #2a2a4a)',
        borderRadius: '16px', width: '90vw', maxWidth: '900px', maxHeight: '85vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden'
      }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: '1px solid var(--card-border, #2a2a4a)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FileText size={18} style={{ color: 'var(--accent, #3b82f6)' }} />
            <span style={{ fontWeight: 600, color: 'var(--text, #fff)', fontSize: '15px' }}>
              {fileName || data?.filename || 'Fichier'}
            </span>
            {badge && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px',
                padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 500,
                background: `${badge.color}20`, color: badge.color
              }}>
                {badge.icon} {badge.label}
              </span>
            )}
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: 'var(--text-secondary, #888)', cursor: 'pointer', padding: '4px'
          }}><X size={20} /></button>
        </div>

        {/* Metadata bar */}
        {data && (
          <div style={{
            display: 'flex', gap: '16px', padding: '10px 20px', fontSize: '12px',
            color: 'var(--text-muted, #666)', borderBottom: '1px solid var(--card-border, #2a2a4a)',
            flexWrap: 'wrap', alignItems: 'center'
          }}>
            <span>{data.mimetype || 'inconnu'}</span>
            <span>{data.content_length ? `${Math.round(data.content_length / 1024)} Ko texte` : ''}</span>
            {data.chunk_count > 0 && <span>{data.chunk_count} chunks</span>}
            {data.page_count && <span>{data.page_count} pages</span>}
            <span>Source: {data.source_type}{data.is_reconstructed ? ' (reconstruit)' : ''}</span>
            {data.is_truncated_for_preview && (
              <span style={{ color: '#f59e0b' }}>Preview tronquée</span>
            )}

            {/* View toggle */}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '4px' }}>
              <button onClick={() => setViewMode('text')} style={{
                padding: '3px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '11px',
                background: viewMode === 'text' ? 'var(--accent, #3b82f6)' : 'var(--card-bg, #222)',
                color: viewMode === 'text' ? '#fff' : 'var(--text-secondary, #888)'
              }}>Texte</button>
              <button onClick={() => setViewMode('markdown')} style={{
                padding: '3px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '11px',
                background: viewMode === 'markdown' ? 'var(--accent, #3b82f6)' : 'var(--card-bg, #222)',
                color: viewMode === 'markdown' ? '#fff' : 'var(--text-secondary, #888)'
              }}>Markdown</button>
            </div>
          </div>
        )}

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
          {loading && <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '40px' }}>Chargement...</div>}
          {error && <div style={{ textAlign: 'center', color: '#ef4444', padding: '40px' }}>{error}</div>}
          {data && (
            <pre style={{
              whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace',
              fontSize: '13px', lineHeight: '1.6', color: 'var(--text, #ddd)',
              margin: 0, background: 'transparent'
            }}>
              {data.content || '(aucun contenu)'}
            </pre>
          )}
        </div>

        {/* Analyses précédentes — cliquables */}
        {data?.analyses?.length > 0 && (
          <div style={{
            padding: '12px 20px', borderTop: '1px solid var(--card-border, #2a2a4a)',
            fontSize: '12px', color: 'var(--text-muted, #666)', maxHeight: expandedAnalysis ? '300px' : '120px',
            overflowY: 'auto', transition: 'max-height 0.3s'
          }}>
            <div style={{ fontWeight: 600, marginBottom: '6px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Clock size={12} /> Analyses précédentes ({data.analyses.length})
            </div>
            {data.analyses.map(a => (
              <div key={a.id}>
                <div
                  onClick={() => setExpandedAnalysis(expandedAnalysis === a.id ? null : a.id)}
                  style={{
                    display: 'flex', gap: '10px', padding: '5px 8px', cursor: 'pointer',
                    borderRadius: '4px', alignItems: 'center',
                    background: expandedAnalysis === a.id ? 'rgba(37,99,235,0.08)' : 'transparent',
                    transition: 'background 0.15s'
                  }}
                >
                  <span style={{ color: 'var(--accent)', fontWeight: 500 }}>
                    {a.analysis_type === 'analyze' ? '🔍 Analyse' : '📋 Résum��'}
                  </span>
                  <span style={{ color: 'var(--text-secondary)' }}>{a.model}</span>
                  <span>{new Date(a.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                  <span style={{ color: a.source_quality === 'full' ? '#16a34a' : '#f59e0b', fontWeight: 500 }}>
                    {a.source_quality === 'full' ? <CheckCircle size={11} /> : <AlertTriangle size={11} />}
                  </span>
                  <span style={{ marginLeft: 'auto', color: 'var(--text-secondary)', fontSize: '10px' }}>
                    {expandedAnalysis === a.id ? '▲' : '▼'}
                  </span>
                </div>
                {expandedAnalysis === a.id && (a.result_text || a.result_summary) && (
                  <div style={{
                    padding: '8px 12px', margin: '4px 0 8px', borderRadius: '6px',
                    background: 'var(--input-bg, #111)', border: '1px solid var(--card-border, #2a2a4a)',
                    fontSize: '12px', lineHeight: '1.5', color: 'var(--text, #ddd)',
                    whiteSpace: 'pre-wrap', maxHeight: '180px', overflowY: 'auto'
                  }}>
                    {a.result_text || a.result_summary}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Footer actions */}
        <div style={{
          display: 'flex', gap: '10px', padding: '14px 20px',
          borderTop: '1px solid var(--card-border, #2a2a4a)', justifyContent: 'flex-end'
        }}>
          <button onClick={onClose} style={{
            padding: '8px 18px', borderRadius: '8px', border: '1px solid var(--card-border)',
            background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '13px'
          }}>Fermer</button>
          {data?.content && (
            <button onClick={() => {
              const blob = new Blob([data.content], { type: 'text/plain;charset=utf-8' });
              const a = document.createElement('a');
              a.href = URL.createObjectURL(blob);
              a.download = fileName || 'fichier.txt';
              a.click();
              URL.revokeObjectURL(a.href);
            }} style={{
              padding: '8px 18px', borderRadius: '8px', border: '1px solid var(--card-border)',
              background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '13px',
              display: 'flex', alignItems: 'center', gap: '6px'
            }}><Download size={14} /> Télécharger</button>
          )}
          <button onClick={() => onAnalyze && onAnalyze(fileId, fileName, 'summary')} style={{
            padding: '8px 18px', borderRadius: '8px', border: 'none',
            background: '#8b5cf6', color: '#fff', cursor: 'pointer', fontSize: '13px',
            display: 'flex', alignItems: 'center', gap: '6px'
          }}><BarChart3 size={14} /> Résumer</button>
          <button onClick={() => onAnalyze && onAnalyze(fileId, fileName, 'analyze')} style={{
            padding: '8px 18px', borderRadius: '8px', border: 'none',
            background: 'var(--accent, #3b82f6)', color: '#fff', cursor: 'pointer', fontSize: '13px',
            display: 'flex', alignItems: 'center', gap: '6px'
          }}><Search size={14} /> Analyser</button>
        </div>
      </div>
    </div>
  );
};

export default FilePreviewModal;
